/**
 * The permission catalog — layer 3 of `07-entitlements-rbac.md`. "Is this user
 * allowed to perform this action?", asked after the merchant's entitlement and
 * quota have already passed.
 *
 * The source system's 33 keys are action-scoped but inconsistently named
 * (`saverOrUpdateGoodsGroup` is a typo that shipped; `delGoods` beside
 * `deletePrinter`). Ours is a strict `resource:action` scheme. Sensitive
 * actions — refund, void, discount, price change, drawer — stay their own key
 * rather than folding into `write`, because those are precisely the ones a
 * restaurant owner wants to restrict.
 */

import type { MessageKey } from "../i18n/messages/en.ts";

type PermissionDefinition = {
  readonly key: string;
  readonly name: string;
  readonly realm: PermissionRealm;
};

export const PERMISSION_REALMS = ["merchant", "operator"] as const;
export type PermissionRealm = (typeof PERMISSION_REALMS)[number];

const DEFINITIONS = [
  // Menu
  { key: "items:read", name: "View items", realm: "merchant" },
  { key: "items:write", name: "Create and edit items", realm: "merchant" },
  { key: "items:delete", name: "Delete items", realm: "merchant" },
  { key: "items:price", name: "Change item prices", realm: "merchant" },
  { key: "categories:read", name: "View categories", realm: "merchant" },
  { key: "categories:write", name: "Create and edit categories", realm: "merchant" },
  { key: "categories:delete", name: "Delete categories", realm: "merchant" },
  { key: "modifiers:read", name: "View specifications, toppings and tags", realm: "merchant" },
  { key: "modifiers:write", name: "Edit specifications, toppings and tags", realm: "merchant" },
  { key: "modifiers:delete", name: "Delete specifications, toppings and tags", realm: "merchant" },

  // Tables
  { key: "tables:read", name: "View tables and areas", realm: "merchant" },
  { key: "tables:write", name: "Create and edit tables and areas", realm: "merchant" },
  { key: "tables:delete", name: "Delete tables and areas", realm: "merchant" },

  // Orders and settlement
  { key: "orders:read", name: "View orders", realm: "merchant" },
  { key: "orders:write", name: "Take and amend orders", realm: "merchant" },
  { key: "orders:export", name: "Export orders", realm: "merchant" },
  { key: "orders:discount", name: "Apply a discount", realm: "merchant" },
  { key: "orders:void", name: "Void an order or a line", realm: "merchant" },
  { key: "orders:refund", name: "Refund", realm: "merchant" },
  { key: "orders:reverse", name: "Reverse a completed check-out", realm: "merchant" },
  { key: "orders:tax_exempt", name: "Settle an order tax-free", realm: "merchant" },
  // Its own, because a tip adjust changes what somebody is paid. A cashier who
  // may take a tip is not thereby somebody who may go back and change one.
  { key: "orders:tip_adjust", name: "Adjust a tip after payment", realm: "merchant" },
  { key: "hr:read", name: "View jobs and timesheets", realm: "merchant" },
  { key: "hr:write", name: "Manage jobs and correct timesheets", realm: "merchant" },
  // Separate from `hr:write` on purpose: everybody clocks themselves in, and
  // almost nobody may change what a timesheet says afterwards.
  { key: "hr:clock", name: "Clock in and out", realm: "merchant" },
  // Its own, because finalising is the irreversible half: a draft recomputes
  // freely, and a final distribution is what somebody is paid on.
  { key: "hr:tip_finalise", name: "Finalise a tip distribution", realm: "merchant" },
  // Its own, and above `chain:write`: raising a charge against a franchisee is
  // not the same act as renaming a region.
  { key: "chain:royalty", name: "Set royalties and raise charges", realm: "merchant" },
  // Issuing a card creates money the merchant owes. Its own permission, above
  // reading one, for the same reason a refund has one.
  // One pair for bookings and the waitlist together, because one person does
  // both jobs: the host at the door takes the phone call, writes the name on
  // the list and decides who sits. Splitting them would model an org chart no
  // restaurant has.
  { key: "reservations:read", name: "View bookings and the waitlist", realm: "merchant" },
  { key: "reservations:write", name: "Take and seat bookings", realm: "merchant" },
  { key: "giftcards:read", name: "Look up a gift card", realm: "merchant" },
  { key: "giftcards:write", name: "Issue and void gift cards", realm: "merchant" },
  { key: "settlement:pay", name: "Take payment", realm: "merchant" },
  { key: "settlement:drawer", name: "Open the cash drawer", realm: "merchant" },
  { key: "shifts:read", name: "View shift records", realm: "merchant" },
  { key: "shifts:write", name: "Open and hand over a shift", realm: "merchant" },

  // Staff and access
  { key: "staff:read", name: "View staff", realm: "merchant" },
  { key: "staff:write", name: "Create and edit staff", realm: "merchant" },
  { key: "staff:delete", name: "Delete staff", realm: "merchant" },
  { key: "roles:read", name: "View roles", realm: "merchant" },
  { key: "roles:write", name: "Create and edit roles", realm: "merchant" },

  // Settings
  { key: "settings:read", name: "View store settings", realm: "merchant" },
  { key: "settings:store", name: "Edit the store profile", realm: "merchant" },
  { key: "settings:tax", name: "Edit tax rules", realm: "merchant" },
  { key: "settings:charges", name: "Edit service charge and table fee", realm: "merchant" },
  { key: "settings:currency", name: "Edit currency and exchange rate", realm: "merchant" },
  { key: "settings:payment_methods", name: "Edit payment methods", realm: "merchant" },
  { key: "printers:read", name: "View printers and print logs", realm: "merchant" },
  { key: "printers:write", name: "Edit, pair and unpair printers", realm: "merchant" },
  { key: "printers:reprint", name: "Reprint a document", realm: "merchant" },
  { key: "terminals:pair", name: "Pair a till", realm: "merchant" },
  { key: "terminals:assign_staff", name: "Put someone on a till", realm: "merchant" },
  { key: "templates:read", name: "View receipt templates", realm: "merchant" },
  { key: "templates:write", name: "Edit receipt templates", realm: "merchant" },
  { key: "banners:read", name: "View banners", realm: "merchant" },
  { key: "banners:write", name: "Create and edit banners", realm: "merchant" },
  { key: "banners:delete", name: "Delete banners", realm: "merchant" },

  // Marketing and members
  { key: "marketing:read", name: "View coupons and promotions", realm: "merchant" },
  { key: "marketing:write", name: "Create and edit coupons and promotions", realm: "merchant" },
  { key: "members:read", name: "View members", realm: "merchant" },
  { key: "members:write", name: "Create and edit members", realm: "merchant" },
  { key: "members:balance", name: "Top up and adjust a member balance", realm: "merchant" },
  { key: "members:points", name: "Adjust member points", realm: "merchant" },

  // Inventory has its own set entirely — see `psi-permissions.ts`. The source
  // keeps `permissions` and `psi_permissions` in two columns, and a key that
  // could sit in either array is a key somebody will put in the wrong one.

  // Reporting and audit
  { key: "reports:read", name: "View reports", realm: "merchant" },
  { key: "reports:export", name: "Export reports", realm: "merchant" },
  { key: "reports:cost", name: "View cost and profit reports", realm: "merchant" },
  { key: "audit:read", name: "View the operation log", realm: "merchant" },

  // Merchant-facing billing
  { key: "billing:read", name: "View the subscription", realm: "merchant" },
  { key: "billing:manage", name: "Sign, renew and buy add-ons", realm: "merchant" },

  // Chain
  { key: "chain:read", name: "View chain rollups", realm: "merchant" },
  { key: "chain:write", name: "Configure stores in the chain", realm: "merchant" },

  // Operator realm
  { key: "admin:merchants:read", name: "View merchants", realm: "operator" },
  { key: "admin:merchants:write", name: "Create and edit merchants", realm: "operator" },
  { key: "admin:plans:read", name: "View plan versions and features", realm: "operator" },
  { key: "admin:plans:write", name: "Edit plan versions and features", realm: "operator" },
  { key: "admin:contracts:read", name: "View contracts", realm: "operator" },
  { key: "admin:contracts:sign", name: "Sign and renew contracts", realm: "operator" },
  { key: "admin:subscriptions:read", name: "View subscriptions", realm: "operator" },
  {
    key: "admin:subscriptions:write",
    name: "Provision, upgrade and renew subscriptions",
    realm: "operator",
  },
  { key: "admin:subscriptions:invalidate", name: "Invalidate a subscription", realm: "operator" },
  { key: "admin:hardware:read", name: "View hardware", realm: "operator" },
  { key: "admin:hardware:write", name: "Edit hardware", realm: "operator" },
  { key: "admin:partners:read", name: "View channel partners", realm: "operator" },
  { key: "admin:partners:write", name: "Edit channel partners", realm: "operator" },
  { key: "admin:settlements:read", name: "View partner settlements", realm: "operator" },
  { key: "admin:settlements:complete", name: "Complete a partner settlement", realm: "operator" },
  { key: "admin:reconciliation:read", name: "View reconciliation", realm: "operator" },
  { key: "admin:team:read", name: "View the operator team", realm: "operator" },
  { key: "admin:team:write", name: "Edit the operator team and its roles", realm: "operator" },
  { key: "admin:dashboard:read", name: "View the operator dashboard", realm: "operator" },
  { key: "admin:referrals:read", name: "View referrals", realm: "operator" },
  { key: "admin:referrals:write", name: "Update referral status and ownership", realm: "operator" },
] as const satisfies readonly PermissionDefinition[];

export type PermissionKey = (typeof DEFINITIONS)[number]["key"];

export const PERMISSION_CATALOG: readonly PermissionDefinition[] = DEFINITIONS;

export const PERMISSION_KEYS = DEFINITIONS.map((p) => p.key) as unknown as readonly [
  PermissionKey,
  ...PermissionKey[],
];

const BY_KEY = new Map<string, PermissionDefinition>(DEFINITIONS.map((p) => [p.key, p]));

export function isPermissionKey(value: string): value is PermissionKey {
  return BY_KEY.has(value);
}

export function permissionsForRealm(realm: PermissionRealm): readonly PermissionDefinition[] {
  return DEFINITIONS.filter((p) => p.realm === realm);
}

/**
 * The message key a permission's label renders under — `items:read` becomes
 * `permission.items.read`, `psi:stock:count` becomes `permission.psi.stock.count`.
 * Colons are not legal in a message key; dots are what the packs already use.
 * Shared by both catalogues (`permissions.ts` and `psi-permissions.ts`, whose
 * keys are already `psi:`-prefixed) and every screen that lists a permission,
 * so a grant renders in the merchant's own language instead of the catalogue's
 * English `name` — the pattern `asLabel` uses for enums crossing the wire.
 */
export function permissionMessageKey(key: string): MessageKey {
  return `permission.${key.replace(/:/g, ".")}` as MessageKey;
}

/**
 * Permission keys that are grantable before any route enforces them. Same
 * shrink-only contract as `RESERVED_FEATURES`: an entry leaves this list at the
 * moment a `requirePermission(...)` starts checking it.
 */
export const RESERVED_PERMISSIONS: readonly PermissionKey[] = [];
